import React, { useState, useCallback } from 'react';
import { Field, Form, Formik } from 'formik';
import { useAppDispatch } from 'redux/store';
import { TextField } from 'formik-mui';
import { ButtonWithLoader } from 'components/Buttons';
import { useSelector } from 'react-redux';
import { authSelector, beginSMSRegistration, completeSMSRegistration } from 'redux/reducers/auth';
import useTheme from '@mui/material/styles/useTheme';
import { Typography, Button, Box, InputAdornment } from '@mui/material';
import * as Yup from 'yup';
import { formatPhoneDisplay, extractPhoneDigits } from '../../utils/phoneFormat';
import { useHistory, useLocation } from 'react-router-dom';
import { LocationState } from 'utils';
import { getUserData } from '../../redux/reducers/user';
import userResendSMSCode from '../../api/Auth/userResendSMSCode';
import OTPInput, { InputProps } from 'react-otp-input';
import checkPhoneExists, { ExistingUser } from '../../api/Auth/checkPhoneExists';
import ExistingAccountModal from './ExistingAccountModal';

interface RegisterFormPhoneProps {
    onSwitchToEmail: () => void;
}

// Validation schema for phone registration
const phoneRegistrationSchema = Yup.object({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    phone: Yup.string()
        .required('Phone number is required')
        .test('phone-length', 'Phone number must be 10 digits', (value) => {
            if (!value) return false;
            const digits = extractPhoneDigits(value);
            return digits.length === 10;
        }),
});

// Validation schema for verification code
const verificationCodeSchema = Yup.object({
    code: Yup.string()
        .required('Verification code is required')
        .length(6, 'Verification code must be 6 digits'),
});

const RegisterFormPhone: React.FC<RegisterFormPhoneProps> = ({ onSwitchToEmail }) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { push } = useHistory();
    const location = useLocation<LocationState>();
    const { isLoading } = useSelector(authSelector);

    const [isVerificationStep, setIsVerificationStep] = useState(false);
    const [registrationData, setRegistrationData] = useState<{
        firstName: string;
        lastName: string;
        phone: string;
    } | null>(null);
    const [obscuredPhoneNumber, setObscuredPhoneNumber] = useState<string>('');
    const [resendLoading, setResendLoading] = useState(false);
    const [existingAccountModalOpen, setExistingAccountModalOpen] = useState(false);
    const [existingUsers, setExistingUsers] = useState<ExistingUser[]>([]);
    const [checkedPhone, setCheckedPhone] = useState('');

    const handlePhoneBlur = useCallback(
        async (phoneValue: string) => {
            const digits = extractPhoneDigits(phoneValue);
            if (digits.length !== 10) return;

            const formattedPhone = `+1${digits}`;
            if (formattedPhone === checkedPhone) return;

            try {
                const response = await checkPhoneExists(formattedPhone);
                if (response.data.exists && response.data.matching_users.length > 0) {
                    setExistingUsers(response.data.matching_users);
                    setCheckedPhone(formattedPhone);
                    setExistingAccountModalOpen(true);
                }
            } catch {
                // Silently fail — don't block registration if the check fails
            }
        },
        [checkedPhone],
    );

    const handleExistingAccountConfirm = () => {
        setExistingAccountModalOpen(false);
        push('/login', {
            prefillPhone: checkedPhone,
            preferPhoneLogin: true,
        });
    };

    const handlePhoneRegistrationSubmit = async (
        values: { firstName: string; lastName: string; phone: string },
        { setErrors, setFieldError }: any,
    ) => {
        const phoneDigits = extractPhoneDigits(values.phone);
        const formattedPhone = `+1${phoneDigits}`;

        // Check if phone already exists before proceeding with registration
        try {
            const phoneCheck = await checkPhoneExists(formattedPhone);
            if (phoneCheck.data.exists && phoneCheck.data.matching_users.length > 0) {
                setExistingUsers(phoneCheck.data.matching_users);
                setCheckedPhone(formattedPhone);
                setExistingAccountModalOpen(true);
                return;
            }
        } catch {
            // If the check fails, proceed with registration
        }

        const result = await dispatch(
            beginSMSRegistration({
                firstName: values.firstName,
                lastName: values.lastName,
                phone: formattedPhone,
            }),
        );

        if (beginSMSRegistration.fulfilled.match(result)) {
            setRegistrationData({
                firstName: values.firstName,
                lastName: values.lastName,
                phone: formattedPhone,
            });
            setObscuredPhoneNumber(`(***) ***-${formattedPhone.replace(/\D/g, '').slice(-4)}`);
            setIsVerificationStep(true);
        } else if (beginSMSRegistration.rejected.match(result)) {
            if (result.payload?.errors) {
                setErrors(result.payload.errors);
            } else {
                setFieldError(
                    'phone',
                    result.payload?.message || 'An error occurred. Please try again.',
                );
            }
        }
    };

    const handleSuccessfulRegistration = () => {
        // Navigate to appropriate page
        if (localStorage.getItem('athleteSubscribeUrl')) {
            const redirect = localStorage.getItem('athleteSubscribeUrl');
            localStorage.removeItem('athleteSubscribeUrl');
            window.location.href = redirect || '/home';
        } else if (location.state?.referrer) {
            push(location.state.referrer);
        } else {
            push('/home');
        }

        // Load user data
        dispatch(getUserData());
    };

    const handleVerificationCodeSubmit = async (
        values: { code: string },
        { setErrors, setFieldError }: any,
    ) => {
        if (!registrationData) return;

        const result = await dispatch(
            completeSMSRegistration({
                firstName: registrationData.firstName,
                lastName: registrationData.lastName,
                phone: registrationData.phone,
                code: values.code,
            }),
        );

        if (completeSMSRegistration.fulfilled.match(result)) {
            handleSuccessfulRegistration();
        } else if (completeSMSRegistration.rejected.match(result)) {
            if (result.payload?.errors) {
                setErrors(result.payload.errors);
            } else {
                setFieldError('code', result.payload?.message || 'Invalid verification code.');
            }
        }
    };

    const handleResendCode = async () => {
        if (!registrationData) return;

        setResendLoading(true);
        try {
            await userResendSMSCode(registrationData.phone);
            // You might want to show a success message here
        } catch (error) {
            console.error('Failed to resend code:', error);
            // You might want to show an error message here
        } finally {
            setResendLoading(false);
        }
    };

    const resetRegistration = () => {
        setIsVerificationStep(false);
        setRegistrationData(null);
        setObscuredPhoneNumber('');
    };

    // Verification step
    if (isVerificationStep && registrationData) {
        return (
            <Box>
                <Typography variant="h6" gutterBottom align="center">
                    Verification Code Sent
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    align="center"
                    sx={{ mb: 3 }}
                >
                    We sent a code to {obscuredPhoneNumber}
                </Typography>

                <Formik
                    initialValues={{ code: '' }}
                    validationSchema={verificationCodeSchema}
                    onSubmit={handleVerificationCodeSubmit}
                >
                    {({ values, setFieldValue, isValid, dirty, errors, touched }) => (
                        <Form>
                            <Box sx={{ mb: 3 }}>
                                <OTPInput
                                    value={values.code}
                                    onChange={(value: string) => setFieldValue('code', value)}
                                    numInputs={6}
                                    inputType="tel"
                                    shouldAutoFocus={true}
                                    skipDefaultStyles={true}
                                    renderInput={(props: InputProps, index: number) => (
                                        <input
                                            {...props}
                                            disabled={isLoading}
                                            key={index}
                                            style={{
                                                width: '3rem',
                                                height: '3rem',
                                                margin: '0 4px',
                                                textAlign: 'center',
                                                fontSize: '1.5rem',
                                                fontWeight: 'bold',
                                                border: '1px solid #ccc',
                                                borderRadius: '6px',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                outline: 'none',
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = 'black';
                                                e.target.style.borderWidth = '2px';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#ccc';
                                                e.target.style.borderWidth = '1px';
                                            }}
                                        />
                                    )}
                                    containerStyle={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginBottom: '16px',
                                    }}
                                />
                                {errors.code && touched.code && (
                                    <Typography
                                        color="error"
                                        variant="body2"
                                        sx={{ mt: 1, textAlign: 'center' }}
                                    >
                                        {errors.code}
                                    </Typography>
                                )}
                            </Box>

                            <ButtonWithLoader
                                size="large"
                                type="submit"
                                color="primary"
                                variant="contained"
                                sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                                disabled={isLoading ? true : !(isValid && dirty)}
                                loading={isLoading}
                            >
                                {isLoading ? 'Creating Account...' : 'Verify & Create Account'}
                            </ButtonWithLoader>
                        </Form>
                    )}
                </Formik>

                <Button
                    variant="text"
                    onClick={handleResendCode}
                    disabled={resendLoading}
                    sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                >
                    {resendLoading ? 'Resending...' : 'Resend Code'}
                </Button>

                <Button
                    variant="text"
                    onClick={resetRegistration}
                    sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                >
                    Back to Registration
                </Button>

                <Button variant="contained" onClick={onSwitchToEmail} sx={{ width: '100%' }}>
                    Register with email instead
                </Button>
            </Box>
        );
    }

    // Default phone registration form
    return (
        <Formik
            initialValues={{ firstName: '', lastName: '', phone: '' }}
            validationSchema={phoneRegistrationSchema}
            onSubmit={handlePhoneRegistrationSubmit}
        >
            {({ values, setFieldValue, isValid, dirty }) => (
                <Form>
                    <Field
                        sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                        component={TextField}
                        variant="filled"
                        name="firstName"
                        label="First Name"
                    />

                    <Field
                        sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                        component={TextField}
                        variant="filled"
                        name="lastName"
                        label="Last Name"
                    />

                    <Field
                        sx={{ width: '100%', marginBottom: theme.spacing(4) }}
                        component={TextField}
                        variant="filled"
                        name="phone"
                        type="tel"
                        label="Phone Number"
                        placeholder="(555) 555-5555"
                        value={values.phone}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const formattedValue = formatPhoneDisplay(e.target.value);
                            void setFieldValue('phone', formattedValue);
                        }}
                        onBlur={() => {
                            void handlePhoneBlur(values.phone);
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+1</InputAdornment>,
                        }}
                    />

                    <ButtonWithLoader
                        size="large"
                        type="submit"
                        color="primary"
                        variant="contained"
                        sx={{ width: '100%', marginBottom: theme.spacing(4) }}
                        disabled={isLoading ? true : !(isValid && dirty)}
                        loading={isLoading}
                    >
                        {isLoading ? 'Sending Code...' : 'Send Verification Code'}
                    </ButtonWithLoader>

                    <Button variant="contained" onClick={onSwitchToEmail} sx={{ width: '100%' }}>
                        Register with email instead
                    </Button>

                    <ExistingAccountModal
                        open={existingAccountModalOpen}
                        onClose={() => setExistingAccountModalOpen(false)}
                        onConfirm={handleExistingAccountConfirm}
                        matchingUsers={existingUsers}
                    />
                </Form>
            )}
        </Formik>
    );
};

export default RegisterFormPhone;
